import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageMeta from '../components/PageMeta';
import { priceLabel, TRIAL_DAYS, INCLUDED } from '../marketing/pricing';
import { GETTING_STARTED_STEPS, gettingStartedHowToJsonLd } from '../marketing/gettingStarted';
import { useReveal } from './Landing';
import './Landing.css';

// /getting-started (v2.7 US-3.1): ten steps from sign-up to the first
// automatic reminder. Same design system, nav and footer as Landing.js and
// YmcaBasketball.js. The steps live in marketing/gettingStarted.js, which
// also feeds the HowTo JSON-LD and is checked against the in-app checklist.
//
// Prerender safety, as on the other public pages: never `text{' '}<a>`;
// mixed strings are built with template literals.

const PATH = '/getting-started';

const PARENT_STEPS = [
  { title: 'Register with your code', desc: 'Parents open your join link, create a free account and add their own children with jersey numbers, positions and an emergency contact.' },
  { title: 'Sign the waiver', desc: 'If you published one, parents sign it by typing their name before their first game.' },
  { title: 'RSVP and get reminders', desc: 'One tap per child for each game. Reminders arrive by email, as a notification on phones with the app installed, and by text for parents who check the box.' },
];

const GettingStartedGuide = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  useReveal(rootRef);

  return (
    <div className="hh-page" ref={rootRef}>
      <PageMeta
        title="Getting Started with HoopHero — Set Up Your Youth Basketball Program in 10 Steps"
        description={`Ten steps from sign-up to your first automatic game reminder: program type, fee, payment methods, gym, schedule, waiver, invite link and your first family. About ten minutes. ${TRIAL_DAYS}-day free trial, then ${priceLabel()}.`}
        path={PATH}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(gettingStartedHowToJsonLd())}
        </script>
      </Helmet>

      <nav className="hh-nav" aria-label="Site">
        <a className="hh-nav-logo" href="/" aria-label="HoopHero home">
          <img
            src="/logo-wide.png"
            alt="HoopHero — youth basketball program management software"
            className="hh-nav-logo-img"
            width="1119"
            height="390"
          />
        </a>
        <div className="hh-nav-links">
          <button className="hh-nav-link hh-nav-link--coach" onClick={() => navigate('/coach/login')}>Coach login</button>
          <button className="hh-nav-link hh-nav-link--parent" onClick={() => navigate('/parent/login')}>Parent login</button>
          <button className="hh-nav-cta" onClick={() => navigate('/coach/register')}>Start free trial</button>
        </div>
      </nav>

      <main>
        <header className="hh-hero hh-hero--compact">
          <div className="hh-hero-inner">
            <div className="hh-hero-content">
              <h1 className="hh-hero-title">
                {'Getting started: '}
                <span className="hh-hero-accent">ten steps, about ten minutes.</span>
              </h1>
              <p className="hh-hero-sub">
                From sign-up to your first automatic game reminder. The same steps tick themselves off on the Getting Started card on your coach dashboard as you go.
              </p>
              <div className="hh-hero-btns">
                <button className="hh-btn hh-btn--primary hh-btn--lg" onClick={() => navigate('/coach/register')}>
                  Start free trial
                </button>
                <button className="hh-btn hh-btn--ghost-light hh-btn--lg" onClick={() => navigate('/coach/login')}>
                  I already have an account
                </button>
              </div>
            </div>
          </div>
        </header>

        <section className="hh-plan">
          <div className="hh-plan-inner hh-guide-inner">
            <h2 className="hh-h2">Set up your program</h2>
            <p className="hh-lead">
              Running a Free or Free (YMCA) program? Skip steps 4 and 5: there is no fee and nothing to collect.
            </p>
            <ol className="hh-guide">
              {GETTING_STARTED_STEPS.map((s, i) => (
                <li key={s.id} id={`step-${s.id}`} className="hh-guide-step">
                  <span className="hh-step-num" aria-hidden="true">{i + 1}</span>
                  <div>
                    <h3 className="hh-step-title">{s.title}</h3>
                    <p className="hh-step-desc">{s.body}</p>
                    <p className="hh-guide-where">{`Where: ${s.where}`}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="hh-features">
          <div className="hh-plan-inner hh-guide-inner" data-reveal>
            <h2 className="hh-h2">What your parents do</h2>
            <ol className="hh-guide hh-guide--short">
              {PARENT_STEPS.map((s, i) => (
                <li key={s.title} className="hh-guide-step">
                  <span className="hh-step-num" aria-hidden="true">{i + 1}</span>
                  <div>
                    <h3 className="hh-step-title">{s.title}</h3>
                    <p className="hh-step-desc">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="hh-lead">
              Parent accounts are always free. Parents can install HoopHero on their home screen from the browser, no app store needed.
            </p>
          </div>
        </section>

        <section className="hh-final">
          <div className="hh-final-inner" data-reveal>
            <h2 className="hh-display hh-display--center">Ready to set up your season?</h2>
            <p className="hh-lead hh-lead--center">
              {`${TRIAL_DAYS}-day free trial, no credit card needed to start. Then ${priceLabel()} for ${INCLUDED}. Cancel anytime.`}
            </p>
            <button className="hh-btn hh-btn--primary hh-btn--lg" onClick={() => navigate('/coach/register')}>
              Start free trial
            </button>
          </div>
        </section>
      </main>

      <footer className="hh-footer">
        <div className="hh-footer-inner">
          <img
            src="/logo-wide.png"
            alt="HoopHero — youth basketball program management software"
            className="hh-footer-logo-img"
            width="1119"
            height="390"
            loading="lazy"
          />
          <div className="hh-footer-links">
            <button className="hh-footer-link" onClick={() => navigate('/')}>Home</button>
            <button className="hh-footer-link" onClick={() => navigate('/coach/login')}>Coach login</button>
            <button className="hh-footer-link" onClick={() => navigate('/parent/login')}>Parent login</button>
            <button className="hh-footer-link" onClick={() => navigate('/coach/register')}>Start free trial</button>
            <button className="hh-footer-link" onClick={() => navigate('/terms')}>Terms</button>
            <button className="hh-footer-link" onClick={() => navigate('/privacy')}>Privacy</button>
            <button className="hh-footer-link" onClick={() => navigate('/sms')}>Text messages</button>
          </div>
          <div className="hh-footer-note">© 2026 Clarity Growth Pro LLC. HoopHero is built for youth basketball coaches.</div>
        </div>
      </footer>
    </div>
  );
};

export default GettingStartedGuide;
